import React, { useState } from "react";

import { BestSellingProducts, CostCoverage, DeliveryReconciliation } from "./SalesIntelligenceDetails";
import { useFetchBiOverviewQuery } from "../../../api/analyticsApi";
import { Btn, Segmented } from "../../catalogProductStudio/edit/StudioUi";
import { TrendChart } from "../AnalyticsCharts";
import { BarList, DataNotes, DataState, KpiCard, Panel } from "../AnalyticsUi";
import { costBasisLabel, fill, formatDateTime, formatMoney, formatNumber, formatPercent } from "../analyticsModel";

export const OverviewKpis = ({ data, tt, lang, costVisible: allowed }) => {
  const { kpis } = data;
  const costVisible = allowed && data.access?.costVisible;
  return (
    <div className="an-kpis">
      <KpiCard tt={tt} lang={lang} tone="accent" label={tt("kpi_netRevenue")} help={tt("kpi_netRevenue_help")} compared={kpis.netRevenue} display={formatMoney(kpis.netRevenue.value, lang)} />
      <KpiCard tt={tt} lang={lang} label={tt("kpi_totalRevenue")} help={tt("kpi_totalRevenue_help")} compared={kpis.totalRevenue} display={formatMoney(kpis.totalRevenue.value, lang)} />
      <KpiCard
        tt={tt}
        lang={lang}
        label={tt("kpi_grossProfit")}
        help={tt("kpi_grossProfit_help")}
        restricted={!costVisible}
        compared={kpis.grossProfit}
        display={formatMoney(kpis.grossProfit?.value, lang)}
        badge={costVisible ? costBasisLabel(tt, data.cost) : null}
        sub={costVisible && data.cost?.status === "partial" ? fill(tt("coverage"), { value: formatPercent(data.cost.coverage, lang) }) : null}
      />
      <KpiCard
        tt={tt}
        lang={lang}
        label={tt("kpi_grossMargin")}
        help={tt("kpi_grossMargin_help")}
        restricted={!costVisible}
        compared={kpis.grossMargin}
        display={formatPercent(kpis.grossMargin?.value, lang)}
        badge={costVisible ? costBasisLabel(tt, data.cost) : null}
      />
      <KpiCard tt={tt} lang={lang} label={tt("kpi_salesCount")} help={tt("kpi_salesCount_help")} compared={kpis.salesCount} display={formatNumber(kpis.salesCount.value, lang)} />
      <KpiCard tt={tt} lang={lang} label={tt("kpi_averageOrderValue")} help={tt("kpi_averageOrderValue_help")} compared={kpis.averageOrderValue} display={formatMoney(kpis.averageOrderValue.value, lang)} />
      <KpiCard tt={tt} lang={lang} label={tt("kpi_unitsSold")} help={tt("kpi_unitsSold_help")} compared={kpis.unitsSold} display={formatNumber(kpis.unitsSold.value, lang)} />
      <KpiCard
        tt={tt}
        lang={lang}
        invert
        label={tt("kpi_refunds")}
        help={tt("kpi_refunds_help")}
        compared={kpis.refunds}
        display={formatMoney(kpis.refunds.value, lang)}
        sub={fill(tt("returnsDetail"), { count: kpis.returnsCount.value ?? 0, units: kpis.unitsReturned.value ?? 0 })}
      />
    </div>
  );
};

const OverviewSection = ({ range, tt, lang, costVisible }) => {
  const result = useFetchBiOverviewQuery(
    { dateFrom: range.dateFrom, dateTo: range.dateTo, granularity: range.granularity },
    { pollingInterval: 60_000, skipPollingIfUnfocused: true, refetchOnMountOrArgChange: true },
  );
  const query = { ...result, data: result.currentData };
  const [metric, setMetric] = useState("netRevenue");
  const data = query.data;
  const isEmpty = data && data.kpis.totalRevenue.value === 0 && data.kpis.refunds.value === 0;

  return (
    <DataState query={query} tt={tt}>
      {data ? (
        <>
          <div className="an-freshness" role="status">
            <span>{fill(tt("freshnessLabel"), { at: formatDateTime(data.generatedAt, lang), seconds: data.freshness?.cacheTtlSeconds ?? 60 })} · {data.range.timezone} · {data.currency}</span>
            <Btn size="sm" icon="refresh" disabled={query.isFetching} onClick={() => query.refetch()}>{query.isFetching ? tt("loading") : tt("refreshData")}</Btn>
          </div>
          <OverviewKpis data={data} tt={tt} lang={lang} costVisible={costVisible} />
          <DataNotes notes={data.notes} tt={tt} lang={lang} />
          <DeliveryReconciliation data={data.reconciliation} tt={tt} lang={lang} />
          {costVisible && data.access?.costVisible ? <CostCoverage cost={data.cost} tt={tt} lang={lang} /> : null}
          {isEmpty ? (
            <div className="an-empty">
              <strong>{tt("emptyTitle")}</strong>
              <span>{tt("emptyBody")}</span>
            </div>
          ) : (
            <>
              <Panel
                id="an-trend"
                title={metric === "salesCount" ? tt("salesVolume") : tt("revenueTrend")}
                hint={tt("revenueTrendHint")}
                actions={
                  <Segmented
                    ariaLabel={tt("revenueTrend")}
                    value={metric}
                    onChange={setMetric}
                    options={[
                      { value: "revenue", label: tt("revenue") },
                      { value: "netRevenue", label: tt("netRevenue") },
                      { value: "salesCount", label: tt("sales") },
                    ]}
                  />
                }
              >
                <TrendChart
                  points={data.trend}
                  field={metric}
                  granularity={data.granularity}
                  label={metric === "salesCount" ? tt("salesVolume") : metric === "netRevenue" ? tt("kpi_netRevenue") : tt("kpi_totalRevenue")}
                  money={metric !== "salesCount"}
                  lang={lang}
                  tt={tt}
                  height={300}
                />
              </Panel>
              <div className="an-grid an-grid--2">
                <Panel id="an-channel" title={tt("byChannel")} hint={tt("salesChannelHint")}>
                  <BarList
                    rows={(data.salesChannels || []).map((row) => ({ ...row, key: row.channel }))}
                    valueKey="netRevenue"
                    labelOf={(row) => tt(`channel_${row.channel}`)}
                    format={(value) => formatMoney(value, lang)}
                    secondary={(row) => `${formatNumber(row.salesCount, lang)} ${tt("sales").toLowerCase()} · ${formatNumber(row.units, lang)} ${tt("units").toLowerCase()}`}
                  />
                </Panel>
                <Panel id="an-category" title={tt("byCategory")} hint={tt("categoryBasisHint")}>
                  <BarList
                    rows={data.categories.slice(0, 5)}
                    valueKey="netRevenue"
                    labelOf={(row) => row.label || tt("uncategorised")}
                    format={(value, row) => `${formatMoney(value, lang)} · ${formatPercent(row.share, lang)}`}
                    secondary={(row) => `${formatNumber(row.units, lang)} ${tt("units").toLowerCase()}`}
                    emptyText={tt("emptyTitle")}
                  />
                </Panel>
              </div>
            </>
          )}
          <BestSellingProducts range={range} tt={tt} lang={lang} />
        </>
      ) : null}
    </DataState>
  );
};

export default OverviewSection;
